import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { listarCotizaciones, filtrarCotizaciones, limpiarCotizaciones, generarPdfCotizacion } from '../../../actions/cotizaciones';

import EliminarCotizacionModal from './modals/EliminarCotizacionModal';

const opcionesStatus = [
    { value: true, label: 'Autorizada' },
    { value: false, label: 'Pendiente' }
];

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const formatMoney = (n) => {
    const v = Number(n || 0);
    return `$${new Intl.NumberFormat('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(v)}`;
};

const renderBadgeStatus = (status) => (
    status
        ? <span className="badge bg-success">Autorizada</span>
        : <span className="badge bg-warning text-dark">Pendiente</span>
);

const CotizacionesProyecto = ({ idProyecto }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { filtrado } = useSelector(state => state.cotizaciones);

    const [textoFiltro, setTextoFiltro] = useState('');
    const [statusSel, setStatusSel] = useState(null);
    const [cotizacionEliminar, setCotizacionEliminar] = useState(null);

    useEffect(() => {
        if (!idProyecto) return;

        dispatch(listarCotizaciones({ id_proyecto: idProyecto }));

        return () => {
            dispatch(limpiarCotizaciones());
        };
    }, [dispatch, idProyecto]);

    const onChangeFiltroTabla = (e) => {
        const val = e.target.value;
        setTextoFiltro(val);
        dispatch(filtrarCotizaciones(val));
    };

    const onChangeStatus = (opcion) => {
        setStatusSel(opcion);
        dispatch(listarCotizaciones({ id_proyecto: idProyecto, status: opcion?.value ?? null }));
    };

    const onBuscarPorFolioBase = useCallback((folioBase) => {
        if (!folioBase) return;
        setTextoFiltro(folioBase);
        dispatch(filtrarCotizaciones(folioBase));
    }, [dispatch]);

    const columns = useMemo(() => ([
        {
            name: 'Folio',
            selector: row => row?.folio || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Nombre',
            selector: row => row?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Cotización de origen',
            selector: row => (row?.folio_base && row.folio_base !== row.folio) ? row.folio_base : '',
            sortable: true,
            wrap: true,
            grow: 1,
            cell: row => (
                row?.folio_base && row.folio_base !== row.folio
                    ? (
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0"
                            title="Buscar cotización de origen"
                            onClick={() => onBuscarPorFolioBase(row.folio_base)}
                        >
                            {row.folio_base}
                        </button>
                    )
                    : 'No aplica'
            )
        },
        {
            name: 'Fecha',
            selector: row => formatDate(row?.fecha),
            sortable: true,
            wrap: true,
            grow: 1
        },
        {
            name: 'Total',
            selector: row => row?.total || 0,
            sortable: true,
            right: true,
            cell: row => (
                <div style={{ width: '100%', textAlign: 'right' }}>
                    {formatMoney(row?.total)}
                </div>
            ),
            grow: 1
        },
        {
            name: 'Status',
            sortable: true,
            width: '140px',
            cell: row => renderBadgeStatus(row?.status)
        },
        {
            name: 'Opciones',
            width: '260px',
            cell: row => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Ver detalle"
                        onClick={() => navigate(`/proyectos/cotizaciones/detalle/${row._id}`)}
                    >
                        <i className="fas fa-eye text-primary" />
                    </button>

                    {!row?.status && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Editar"
                            onClick={() => navigate(`/proyectos/cotizaciones/editar/${row._id}`)}
                        >
                            <i className="fa fa-edit text-warning" />
                        </button>
                    )}

                    {!row?.status && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Crear nueva versión"
                            onClick={() => navigate(`/proyectos/cotizaciones/version/${row._id}`)}
                        >
                            <i className="fas fa-code-branch text-success" />
                        </button>
                    )}

                    {!row?.status && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link px-2"
                            title="Generar orden de salida"
                            onClick={() => navigate(`/proyectos/ordenes/nueva/${idProyecto}?cotizacion=${row._id}`)}
                        >
                            <i className="fas fa-dolly text-info" />
                        </button>
                    )}

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Generar PDF"
                        onClick={() => dispatch(generarPdfCotizacion(row._id))}
                    >
                        <i className="fas fa-file-pdf text-secondary" />
                    </button>

                    <button
                        type="button"
                        className="btn btn-sm btn-link px-2"
                        title="Eliminar"
                        onClick={() => setCotizacionEliminar(row)}
                    >
                        <i className="fas fa-trash text-danger" />
                    </button>
                </div>
            )
        }
    ]), [navigate, onBuscarPorFolioBase, dispatch, idProyecto]);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    return (
        <Fragment>
            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex align-items-center gap-2 w-100 justify-content-end">
                    <div style={{ minWidth: 280 }}>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar en la tabla..."
                            value={textoFiltro}
                            onChange={onChangeFiltroTabla}
                        />
                    </div>

                    <div style={{ minWidth: 200 }}>
                        <Select
                            options={opcionesStatus}
                            value={statusSel}
                            onChange={onChangeStatus}
                            isClearable
                            placeholder="Status..."
                            noOptionsMessage={() => 'Sin resultados'}
                        />
                    </div>

                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => navigate(`/proyectos/cotizaciones/nueva/${idProyecto}`)}
                    >
                        <i className="feather icon-plus" /> Agregar cotización
                    </button>
                </div>
            </div>

            <div className="mt-2">
                <DataTable
                    columns={columns}
                    data={filtrado || []}
                    pagination
                    paginationComponentOptions={paginationOptions}
                    highlightOnHover
                    striped
                    responsive
                    persistTableHead
                    noDataComponent="Sin cotizaciones para mostrar"
                />
            </div>

            <EliminarCotizacionModal
                show={!!cotizacionEliminar}
                idProyecto={idProyecto}
                idCotizacion={cotizacionEliminar?._id || null}
                onHide={() => setCotizacionEliminar(null)}
            />
        </Fragment>
    );
};

export default CotizacionesProyecto;
